import { DateTime } from "luxon";
import type React from "react";
import { useMemo } from "react";
import {
  MonthCalendarGrid,
  MonthDayCell,
} from "@/domains/appointment/appointments/components/calendar/CalendarGrid";
import {
  getBookingStatusInfo,
  getOutcomeStatusInfo,
} from "@/domains/appointment/appointments/shared/utils/appointment-status-style";
import type { AppointmentBookingStatus } from "@/gql/graphql";
import { timezoneUtils } from "@/lib/timezone";
import { useCalendarAppointments } from "../../hooks/use-calendar-appointments";
import { useCalendarFilters } from "../../hooks/use-calendar-filters";
import type { AppointmentListFragment } from "../../types";

interface MonthViewProps {
  currentDate: DateTime;
  onDateChange?: (date: DateTime) => void;
  onAppointmentClick?: (appointmentId: string) => void;
}

export function MonthView({ currentDate, onDateChange, onAppointmentClick }: MonthViewProps) {
  const { statusFilters, workerFilters } = useCalendarFilters();

  // Helper function to get status info for appointments
  const getAppointmentStatusInfo = (appointment: AppointmentListFragment) => {
    // Check if appointment has an outcome (completed, no show, etc.)
    if (appointment.outcome) {
      return getOutcomeStatusInfo(appointment.outcome as any);
    }

    // Otherwise use booking status (pending, confirmed, declined)
    if (appointment.bookingStatus) {
      return getBookingStatusInfo(appointment.bookingStatus as any);
    }

    // Default fallback
    return getBookingStatusInfo("PENDING");
  };

  // Calculate month date range
  const monthStart = currentDate.startOf("month");
  const monthEnd = currentDate.endOf("month");

  // Calendar grid starts from Sunday of the week containing month start
  const calendarStart = monthStart.startOf("week");
  const calendarEnd = monthEnd.endOf("week");

  // Fetch appointments for the entire calendar month view (6 weeks)
  const { appointments } = useCalendarAppointments({
    startDate: calendarStart,
    endDate: calendarEnd,
    statusFilters: statusFilters as AppointmentBookingStatus[],
    workerFilters,
  });

  // Generate calendar days (6 weeks = 42 days)
  const calendarDays = useMemo(() => {
    const days: DateTime[] = [];
    let current = calendarStart;

    for (let i = 0; i < 42; i++) {
      days.push(current);
      current = current.plus({ days: 1 });
    }

    return days;
  }, [calendarStart]);

  // Group appointments by date
  const appointmentsByDate = useMemo(() => {
    const groups: Record<string, AppointmentListFragment[]> = {};

    appointments.forEach((appointment) => {
      const dateKey = timezoneUtils.fromISO(appointment.startTime).toISODate() ?? "";

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(appointment);
    });

    return groups;
  }, [appointments]);

  const getAppointmentsForDay = (date: DateTime) => {
    const dateKey = date.toISODate() ?? "";
    return appointmentsByDate[dateKey] || [];
  };

  const isCurrentMonth = (date: DateTime) => {
    return date.month === currentDate.month;
  };

  const isToday = (date: DateTime) => {
    return date.hasSame(DateTime.now(), "day");
  };

  const handleDayClick = (date: DateTime) => {
    onDateChange?.(date);
  };

  const handleAppointmentClick = (appointment: AppointmentListFragment, e: React.MouseEvent) => {
    e.stopPropagation();
    onAppointmentClick?.(appointment.id);
  };

  const weekdayHeaders = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Month Header */}
      <div className="my-2 text-center flex-shrink-0">
        <h2 className="text-2xl font-bold text-foreground">{currentDate.toFormat("MMMM yyyy")}</h2>
      </div>

      {/* Scrollable Calendar Content with sticky weekday headers */}
      <div className="flex-1 overflow-auto">
        {/* Weekday Headers - Sticky */}
        <div className="sticky top-0 z-50 border-b border-border bg-background">
          <div className="grid grid-cols-7 bg-muted">
            {weekdayHeaders.map((day) => (
              <div
                key={day}
                className="p-3 text-center text-sm font-medium text-foreground border-r border-border last:border-r-0"
              >
                {day}
              </div>
            ))}
          </div>
        </div>

        <MonthCalendarGrid showWeekdayHeaders={false}>
          {calendarDays.map((date) => {
            const dayAppointments = getAppointmentsForDay(date);
            const isCurrentMonthDay = isCurrentMonth(date);
            const isTodayDate = isToday(date);

            return (
              <MonthDayCell
                key={date.toISO()}
                date={date}
                isCurrentMonth={isCurrentMonthDay}
                isToday={isTodayDate}
                minHeight="280px"
                onClick={handleDayClick}
              >
                <div className="space-y-1">
                  {/* Appointment items - show first few as full items */}
                  {dayAppointments.slice(0, 10).map((appointment) => {
                    const statusInfo = getAppointmentStatusInfo(appointment);
                    const workerColor =
                      appointment.appointmentServices?.[0]?.worker?.color || "#6b7280"; // gray-500 fallback
                    const clientName = appointment.customer
                      ? `${appointment.customer.firstName} ${appointment.customer.lastName}`.trim()
                      : "Unknown Client";
                    const serviceName =
                      appointment.appointmentServices?.[0]?.serviceName || "Service";

                    return (
                      <button
                        type="button"
                        key={appointment.id}
                        className={`w-full text-left text-xs p-1 rounded cursor-pointer truncate hover:opacity-90 transition-opacity ${statusInfo.backgroundColor} ${statusInfo.textColor}`}
                        onClick={(e) => handleAppointmentClick(appointment, e)}
                        title={`${clientName} - ${serviceName} at ${timezoneUtils.fromISO(appointment.startTime).toFormat("HH:mm")}`}
                      >
                        <div className="flex items-center gap-1">
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: workerColor,
                            }}
                          />
                          <span className="truncate">
                            {timezoneUtils.fromISO(appointment.startTime).toFormat("HH:mm")}{" "}
                            {clientName}
                          </span>
                        </div>
                      </button>
                    );
                  })}

                  {/* Show number for additional appointments */}
                  {dayAppointments.length > 10 && (
                    <div className="mt-1 text-center">
                      <span className="text-xs text-muted-foreground font-semibold bg-muted px-2 py-1 rounded">
                        +{dayAppointments.length - 10} more
                      </span>
                    </div>
                  )}
                </div>
              </MonthDayCell>
            );
          })}
        </MonthCalendarGrid>
      </div>
    </div>
  );
}
